import PropTypes from 'prop-types'
import React, { useEffect, useRef, useState } from 'react'
import { Button, splitData } from './Button'
import {
  HoverGlidePill,
  SegmentIndicator,
  useSegmentedGlide,
} from './useHoverGlide'

const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']

// Each option keeps its own request's token, so the request settles only its
// own selection, even after its option is dropped and offered again.
function SegmentedOption({
  value,
  buttonRef,
  start,
  settle,
  onChange,
  onFailure,
  ...props
}) {
  const token = useRef(null)
  return (
    <Button
      {...props}
      ref={buttonRef}
      onAction={() => {
        token.current = start(value)
        return onChange(value)
      }}
      onSuccess={() => settle(token.current)}
      onFailure={error => {
        settle(token.current)
        onFailure?.(error)
      }}
      onCancel={() => settle(token.current)}
    />
  )
}

// Controlled: the group shows the requested option while onChange's request is
// pending, then the parent's value prop again once it settles. Arrow keys move
// the single tab stop; Enter, Space or a click commits.
export function SegmentedControl({
  label,
  value,
  options,
  onChange,
  onFailure,
  small = false,
  ...props
}) {
  const [feedback, rest] = splitData(props)
  const [requested, setRequested] = useState(null)
  const [focused, setFocused] = useState(null)
  // The option whose medium label the engine has settled on. The mark only
  // styles a checked option, so a deselected one returns to regular at once.
  const [arrived, setArrived] = useState(null)
  const buttons = useRef(new Map())
  const group = useRef(null)
  // The running request's token.
  const inFlight = useRef(null)
  const pending = requested !== null
  const selected = pending ? requested : value
  const [pill, indicator] = useSegmentedGlide(group, selected, option =>
    setArrived(option ? option.dataset.value : null)
  )
  const enabled = options.filter(option => !option.disabled)
  const stop =
    focused ??
    (options.some(option => option.value === selected)
      ? selected
      : enabled[0]?.value)
  const start = next => {
    const token = {}
    inFlight.current = token
    setRequested(next)
    return token
  }
  const settle = token => {
    if (token !== inFlight.current) return
    inFlight.current = null
    setRequested(null)
  }
  // A refresh that drops the requested option releases the group; that
  // option's request runs on and settles only itself.
  useEffect(() => {
    if (pending && !options.some(option => option.value === requested))
      settle(inFlight.current)
  }, [options, requested])
  const move = (current, key) => {
    const next = window.MEButtons.rovingIndex(
      options,
      options.findIndex(option => option.value === current),
      key,
      option => option.disabled
    )
    if (next < 0) return
    setFocused(options[next].value)
    buttons.current.get(options[next].value).focus()
  }
  return (
    <div
      {...rest}
      ref={group}
      role="radiogroup"
      aria-label={label}
      className={`me-btn-toggle-group${small ? ' me-btn-toggle-sm' : ''}`}
      data-me-react=""
      data-me-hover-react=""
      data-me-glide=""
      aria-busy={pending || undefined}
      aria-disabled={pending || undefined}
      onBlur={event => {
        if (!event.currentTarget.contains(event.relatedTarget)) setFocused(null)
      }}
    >
      <HoverGlidePill ref={pill} />
      <SegmentIndicator ref={indicator} />
      {options.map(option => (
        <SegmentedOption
          {...feedback}
          key={option.value}
          value={option.value}
          buttonRef={node => buttons.current.set(option.value, node)}
          start={start}
          settle={settle}
          onChange={onChange}
          onFailure={onFailure}
          role="radio"
          className="me-btn-toggle"
          aria-checked={selected === option.value}
          aria-disabled={option.disabled || undefined}
          data-me-glide-arrive={
            arrived === option.value ? '' : undefined
          }
          tabIndex={option.value === stop ? 0 : -1}
          data-value={option.value}
          onFocus={() => setFocused(option.value)}
          onKeyDown={event => {
            if (!keys.includes(event.key) || !enabled.length) return
            event.preventDefault()
            move(option.value, event.key)
          }}
          onClick={event => {
            if (inFlight.current !== null || selected === option.value)
              event.preventDefault()
          }}
        >
          {option.label}
        </SegmentedOption>
      ))}
    </div>
  )
}

SegmentedControl.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      disabled: PropTypes.bool,
    })
  ).isRequired,
  // Receives the chosen value and returns the request's promise.
  onChange: PropTypes.func.isRequired,
  onFailure: PropTypes.func,
  small: PropTypes.bool,
}
